import { makeAutoObservable } from 'mobx'
import type {
  IResponsePowerDialer,
  IResponsePowerDialerFilter,
  PowerDialerStatus,
} from 'entities/PowerDialer/api/types'
import { Phone } from 'entities/Phone/model/Phone'
import { Inbox } from 'entities/Inbox/model/Inbox'
import type { IParamsSendFrom, IResponseSendFrom, ISendFromInfo } from 'entities/SendFrom/api/type'
import { getSendFromInfo } from 'entities/SendFrom'
import type { IParamsFiltersList } from 'entities/Broadcast/api/types'
import { PowerDialerStatistics } from 'entities/PowerDialer/model/PowerDialerStatistics'
import type { IEntitiesStore } from 'entities/EntitiesExport'

export class PowerDialer {
  id: number
  name: string
  status: PowerDialerStatus
  sendFrom: IParamsSendFrom
  sendFromNumbersProps: ISendFromInfo | null = null
  inbox: Inbox | null
  number: Phone | null

  filter: IParamsFiltersList = {
    tags: [],
    segments: [],
    contacts: [],
    advanced: [],
  }

  excludeFilter: IParamsFiltersList = {
    tags: [],
    segments: [],
    contacts: [],
    advanced: [],
  }

  startedBy: number
  createdAt: string
  updatedAt: string
  lastRefreshed: string | null = null

  statistics = new PowerDialerStatistics()

  constructor(item: IResponsePowerDialer, private _entities: IEntitiesStore) {
    this.id = item.id
    this.name = item.name
    this.status = item.status
    this.sendFrom = item.send_from
    this.number = item.number ? new Phone(item.number, this._entities) : null
    this.inbox = item.team ? new Inbox(item.team, this._entities) : null
    this.sendFromNumbersProps = this.getSendFromNumbersProps(item.send_from)

    this.startedBy = item.started_by
    this.createdAt = item.created_at
    this.updatedAt = item.updated_at
    this.lastRefreshed = item.last_refreshed ?? null

    this.mapStatistic(item)
    this.mapFilter(item.filter)
    this.mapExcludeFilter(item.filter)

    makeAutoObservable(this)
  }

  get isOnlyManualSelection() {
    const { segments = [], tags = [], advanced = [] } = this.filter
    return segments.length === 0 && tags.length === 0 && advanced.length === 0
  }

  get isDraftStatus() {
    return this.status === 'draft'
  }

  get isSendFromSmartInbox() {
    return this.sendFrom.type === 'from_smart_inbox'
  }

  getSendFromNumbersProps = (sendFrom: IResponseSendFrom) =>
    getSendFromInfo(sendFrom, {
      ...(this.number && { number: this.number }),
      ...(this.inbox && { team: this.inbox }),
    })

  mapFilter = (filter: IResponsePowerDialerFilter) => {
    this.filter = {
      tags: filter.filtersListGroups.tags?.map((tag) => (typeof tag === 'number' ? tag : tag.id)),
      segments: filter.filtersListGroups.segments?.map((segment) =>
        typeof segment === 'number' ? segment : segment.id
      ),
      contacts: filter.filtersListGroups.contacts?.map((contact) =>
        typeof contact === 'number' ? contact : contact.id
      ),
      advanced: filter.filtersListGroups.advanced,
    }
  }

  mapExcludeFilter = (filter: IResponsePowerDialerFilter) => {
    const excludeFiltersListGroups = filter.excludeFiltersListGroups

    if (!excludeFiltersListGroups) {
      this.excludeFilter = { tags: [], segments: [], contacts: [], advanced: [] }
      return
    }

    this.excludeFilter = {
      tags: excludeFiltersListGroups.tags?.map((tag) => (typeof tag === 'number' ? tag : tag.id)),
      segments: excludeFiltersListGroups.segments?.map((segment) =>
        typeof segment === 'number' ? segment : segment.id
      ),
      contacts: excludeFiltersListGroups.contacts?.map((contact) =>
        typeof contact === 'number' ? contact : contact.id
      ),
      advanced: excludeFiltersListGroups.advanced,
    }
  }

  mapStatistic = (item: IResponsePowerDialer) => {
    this.statistics = new PowerDialerStatistics({
      enrolled: item.enrolled,
      answered: item.answered,
      no_answered: item.no_answered,
      contacted: item.contacted,
      no_contacted: item.no_contacted,
      skipped: item.skipped,
    })
  }

  syncOrigin = (item: IResponsePowerDialer) => {
    this.status = item.status
    this.mapFilter(item.filter)
    this.mapExcludeFilter(item.filter)
    this.mapStatistic(item)
  }

  syncStatistic = (item: IResponsePowerDialer) => {
    this.lastRefreshed = item.last_refreshed
    this.mapStatistic(item)
  }
}
